import React, { useEffect, useRef } from "react";
import { Link, redirect, useNavigate, useSearchParams } from "react-router-dom";
import BasicInput from "../inputs/basic";
import Button from "../inputs/button";
import "./login.scss";
import { useState } from "react";
import eyeOff from "../../assets/svgs/eye-off.svg";
import eyeOn from "../../assets/svgs/eyeopen.svg";
import logIn from "../../services/login";
import { login } from "../../services/auth";
import { ForgotPass } from "../../services/forgotPassword";
import { safeNextPath } from "../../services/nextPath";
import { getTags } from "../../services/tag";

// A browser that never chose an ArcTag has no activeSilo. On the dashboard the header
// stores the first one before anything reads it, but a page reached through next reads
// it on load (the Manager asked for /tags/null, the modeled page sent tagId NaN), so
// store it first, the same way the header does. If that fails, the header still will.
async function ensureActiveSilo() {
	if (localStorage.getItem("activeSilo")) return;
	try {
		const tags = (await getTags()).data;
		if (tags?.length) {
			localStorage.setItem("activeSilo", tags[0].tagId);
			localStorage.setItem("publisherSettings", JSON.stringify(tags[0].publisherSettings));
		}
	} catch (e) {
		console.error("Could not choose an ArcTag before returning to the page:", e);
	}
}

function Login() {
	const [email, setEmail] = useState(null);
	const [forgotEmail, setForgotEmail] = useState("");
	const [password, setPassword] = useState(null);
	const [isPassShown, setIsPassShown] = useState(false);
	const [error, setError] = useState(null);
	const [showForgotPass, setShowForgotPass] = useState(false);
	const [loaded, setLoaded] = useState(false);
	const [searchParams] = useSearchParams();
	const errorTimeoutRef = useRef(null);
	const toggleIsPassShown = () => {
		setIsPassShown(!isPassShown);
	};
	const nav = useNavigate();
	const handleSubmit = (event) => {
		event.preventDefault();
		// Clear any existing error timeout
		if (errorTimeoutRef.current) {
			clearTimeout(errorTimeoutRef.current);
			errorTimeoutRef.current = null;
		}
		setError(null);
		setLoaded(true);
		logIn(email, password)
			.then((r) => {
				// Response is ok, proceed with login
				r.json().then(async (session) => {
					login(session);
					// Back to the page that sent the user here, if it is one of ours (ASB-4053)
					const next = safeNextPath(searchParams.get("next"));
					if (next) await ensureActiveSilo();
					nav(next || "/");
				});
			})
			.catch((error) => {
				// Handle error thrown by apiClient for non-ok responses
				const errorMessage = error.data?.message || error.message || "Login failed";
				setError(errorMessage);
				setLoaded(false);
				errorTimeoutRef.current = setTimeout(() => {
					setError("");
					errorTimeoutRef.current = null;
				}, 2000);
			});
	};
	useEffect(() => {
		if (searchParams.get("reason") === "session_expired") {
			setError("Your session has expired. Please log in again.");
		}
	}, []);

	useEffect(() => {
		setForgotEmail(null);
		setEmail(null);
		setPassword(null);
	}, [showForgotPass]);

	const handleForgotPassword = (event) => {
		event.preventDefault();
		// Clear any existing error timeout
		if (errorTimeoutRef.current) {
			clearTimeout(errorTimeoutRef.current);
			errorTimeoutRef.current = null;
		}
		setError("");
		setLoaded(true);
		ForgotPass(forgotEmail)
			.then((r) => {
				// Response is ok, show success message
				r.json().then((data) => {
					setError(data.message || "Recovery email sent successfully");
					setLoaded(false);
					errorTimeoutRef.current = setTimeout(() => {
						setError("");
						errorTimeoutRef.current = null;
					}, 5000);
				});
			})
			.catch((error) => {
				// Handle error thrown by apiClient for non-ok responses
				const errorMessage = error.data?.message || error.message || "Failed to send recovery email";
				setError(errorMessage);
				setLoaded(false);
				errorTimeoutRef.current = setTimeout(() => {
					setError("");
					errorTimeoutRef.current = null;
				}, 5000);
			});
	};

	return (
		<div className="login-container-box">
			{!showForgotPass ? (
				<>
					<form id="login"
						  onSubmit={handleSubmit}>
						<div className="login-inputs">
								<BasicInput
									label="Email"
									value={email}
									id="email"
									type="email"
									name="loginEmail"
									required={true}
									onInput={(e) => setEmail(e.target.value)}
									disabled={false}
									autoComplete="username"
								/>
								<div className="password-input">
									<BasicInput
										label="Password"
										id="password"
										name="password"
										value={password}
										required={true}
										type={isPassShown ? "text" : "password"}
										onInput={(e) => setPassword(e.target.value)}
										disabled={false}
										autoComplete="current-password"
									/>
									{!isPassShown ? (
										<img
											className="passwordIcon"
											src={eyeOff}
											onClick={toggleIsPassShown}
										/>
									) : (
										<img
											className="passwordIcon"
											src={eyeOn}
											onClick={toggleIsPassShown}
										/>
									)}
								</div>
								{error && <div className="login-error">{error}</div>}
						</div>
						<div className="divider" />
						<div className="button-container">
							<Button
								loaded={false}
								style="forgotPass"
								text="Forgot Password"
								onClick={() => setShowForgotPass(true)}
								type="button"
								disabled={false}
							/>

							<Button
								loaded={loaded}
								text="Login"
								style="dark"
								type="submit"
								disabled={false}
							/>
						</div>
					</form>
				</>
			) : (
				<form id="forgotPassword"
					  onSubmit={handleForgotPassword}>
					<div className="login-inputs">
							<h3 className="forgot-pass">A recovery link will be sent to your email.</h3>
							<div className="forgot-container">
								{" "}
								<BasicInput
									label="Email"
									name="forgotEmail"
									id="forgotEmail"
									type="email"
									value={forgotEmail}
									required={true}
									onInput={(e) => setForgotEmail(e.target.value)}
									disabled={false}
									autoComplete="email"
								/>
							</div>
							{error && <div className="login-error">{error}</div>}
						<div className="forgot-divider" />
						<div className="button-forgot-container">
							<Button
								loaded={false}
								text="Back"
								style="forgotPass"
								onClick={() => setShowForgotPass(false)}
								type="button"
								disabled={false}
							/>
							<Button
								loaded={loaded}
								text="Submit"
								style="dark"
								type="submit"
								disabled={loaded}
							/>
						</div>
					</div>
				</form>
			)}
		</div>
	);
}

export default Login;
